// **The main-thread half of the practice worker** (`rounds-practice-sandbox` task 5.12).
//
// `practice.worker.ts` is the device-side composition root; this is the adapter that makes one of them
// satisfy the {@link PracticeSimulationWorker} seam the shipped run controller already calls. It owns
// the thread's lifecycle and its message channel, and it owns nothing else — every rule about
// stepping, quota, ordering and cancellation lives on the other side.
//
// ## Three semantics preserved verbatim, because callers already depend on them
//
// **`cancel()` takes effect within a tick.** It posts, and the driver checks cancellation once per
// stepped tick. The delivery is what the driver's `yieldEveryTicks` macrotask yield exists for: a
// worker that never yielded its own event loop could not *receive* the message, however promptly it
// checked. Sixty-four ticks at 15 Hz is the bound.
//
// **`terminate()` is a cooperative cancel, not a kill.** `Worker.terminate()` stops a thread wherever
// it is — including between `access.write()` and the extent counter that records it. So a terminate
// arriving **while a run is in flight** is forwarded as a message and the real thread teardown waits
// for the run to settle; only then is the thread actually released. The shipped caller
// (`practice-run.ts`) terminates in a `finally` after `await worker.run(...)`, so the common path
// releases immediately, and the deferral only ever engages for a caller that terminates mid-run.
//
// **One worker per run, terminated whatever the outcome.** The thread is constructed inside `run()`
// rather than by the factory, which is what makes a `Worker` constructor that throws — jsdom, a
// browser that refuses module workers, a blocked URL — a typed `{kind:'failed'}` rather than an
// exception thrown past the seam at composition time.

import { practiceWasmGlueUrl } from './practice-wasm';
import type { PracticeWorkerEvent, PracticeWorkerRequest } from './practice.worker';
import type {
  PracticeInFlightView,
  PracticeRunPlan,
  PracticeRunProgress,
  PracticeRunResult,
  PracticeSimulationWorker,
  PracticeWorkerFactory,
} from './practice-runner';

/**
 * The thread, narrowed to what this file uses.
 *
 * A real `Worker` satisfies it structurally. Narrowing it is what lets the protocol be driven in a
 * test — jsdom has no `Worker` at all — without the host growing a second code path for tests.
 */
export interface PracticeWorkerPort {
  postMessage(message: PracticeWorkerRequest): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  addEventListener(type: 'messageerror', listener: (event: unknown) => void): void;
  terminate(): void;
}

export interface PracticeWorkerHostDeps {
  /**
   * Spawns the thread. Defaults to the real module worker.
   *
   * **May throw**, and that is handled: a device with no module-worker support is a device that cannot
   * practise, and the failure has to reach the player as a run outcome rather than as an exception out
   * of the composition root.
   */
  spawn?: () => PracticeWorkerPort;
  /** Where the `waggle-wasm` glue is served from. Resolved here, once, and sent to the worker. */
  glueUrl?: string;
  /** Keep-last-N retention override, threaded to the worker's store. */
  retain?: number;
  /**
   * The store's directory name inside the origin-private root, threaded to the worker's store the
   * same way {@link retain} is. Defaults there to `practice-traces`.
   *
   * Present so `device-benchmark` (design **D-B**) can attach at this seam — the lowest layer that
   * still contains everything it measures — while writing into its **own** root, rather than
   * evicting the player's practice traces and listing its throwaway runs in the sandbox run rail.
   */
  traceRoot?: string;
}

/**
 * The real thread.
 *
 * `new Worker(new URL('./practice.worker.ts', import.meta.url), { type: 'module' })` is the form Vite
 * supports and detects at build time: it bundles the worker as its own chunk and rewrites the URL. The
 * literal must stay inline — a URL held in a variable is not recognised, and the worker would not be
 * emitted at all.
 */
function spawnModuleWorker(): PracticeWorkerPort {
  return new Worker(new URL('./practice.worker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as PracticeWorkerPort;
}

/** One run's worth of device-side execution, over a real thread. */
export function createPracticeWorkerHost(
  deps: PracticeWorkerHostDeps = {},
): PracticeSimulationWorker {
  const spawn = deps.spawn ?? spawnModuleWorker;
  let port: PracticeWorkerPort | null = null;
  let running = false;
  let released = false;
  /** A `terminate()` that arrived mid-run: the thread is released once the run settles. */
  let releaseWhenSettled = false;
  let cancelRequested = false;
  let terminateRequested = false;

  function release(): void {
    if (released) return;
    released = true;
    try {
      port?.terminate();
    } catch {
      // A thread that is already gone is the state we wanted.
    }
  }

  return {
    run(
      plan: PracticeRunPlan,
      onProgress: (progress: PracticeRunProgress) => void,
      onWatch?: (view: PracticeInFlightView) => void,
    ): Promise<PracticeRunResult> {
      if (running || released) {
        // One run per host. A second call would either interleave two seasons over one generation or
        // resurrect a released thread; both are worse than a typed refusal.
        return Promise.resolve({ kind: 'failed', reason: 'unavailable' });
      }
      running = true;

      let spawned: PracticeWorkerPort;
      try {
        spawned = spawn();
      } catch (error) {
        running = false;
        void error;
        // The seam's contract is that a run always answers. A device that cannot start a thread has
        // not crashed mid-season, but `worker_crashed` is the reason the surface already explains as
        // "the simulation stopped unexpectedly on this device", and inventing a new arm of
        // `PracticeRunFailure` for a case the player cannot act on differently would be noise.
        return Promise.resolve({ kind: 'failed', reason: 'worker_crashed' });
      }
      port = spawned;

      return new Promise<PracticeRunResult>((resolve) => {
        let settled = false;
        const settle = (result: PracticeRunResult): void => {
          if (settled) return;
          settled = true;
          running = false;
          if (releaseWhenSettled) release();
          resolve(result);
        };

        spawned.addEventListener('message', (event: { data: unknown }) => {
          const message = event.data as PracticeWorkerEvent;
          if (message?.kind === 'progress') {
            onProgress(message.progress);
            return;
          }
          if (message?.kind === 'watched') {
            // Relayed and nothing more — this file owns the thread's lifecycle, and what a watched
            // view *means* is the coordinator's. A caller that passed no `onWatch` simply drops it,
            // which is the same state a server-executed run is always in.
            onWatch?.(message.view);
            return;
          }
          if (message?.kind === 'result') {
            settle(message.result);
          }
        });
        // A thread that dies — an uncaught error, an un-cloneable message — must surface as a
        // contained failure (`rounds-local-run` "A worker crash is contained"), never as a run that
        // hangs forever waiting for a result nobody will send.
        spawned.addEventListener('error', () => {
          settle({ kind: 'failed', reason: 'worker_crashed' });
        });
        spawned.addEventListener('messageerror', () => {
          settle({ kind: 'failed', reason: 'worker_crashed' });
        });

        spawned.postMessage({
          kind: 'run',
          plan,
          glueUrl: deps.glueUrl ?? practiceWasmGlueUrl(),
          ...(deps.retain === undefined ? {} : { retain: deps.retain }),
          ...(deps.traceRoot === undefined ? {} : { traceRoot: deps.traceRoot }),
        });
        // A cancel or terminate that landed before the run message did is forwarded now, in order, so
        // a cancel pressed the instant Run was is not lost to the spawn's own latency.
        if (cancelRequested) spawned.postMessage({ kind: 'cancel' });
        if (terminateRequested) spawned.postMessage({ kind: 'terminate' });
      });
    },

    cancel(): void {
      cancelRequested = true;
      port?.postMessage({ kind: 'cancel' });
    },

    terminate(): void {
      terminateRequested = true;
      if (running) {
        // Cooperative: the run reclaims its own generation and answers, and the thread is released
        // then. Killing it here could stop it between a write and the extent that records it.
        releaseWhenSettled = true;
        port?.postMessage({ kind: 'terminate' });
        return;
      }
      release();
    },
  };
}

/** The production factory: a fresh thread per run, so a crashed run cannot poison the next one. */
export function createPracticeWorkerFactory(
  deps: PracticeWorkerHostDeps = {},
): PracticeWorkerFactory {
  return () => createPracticeWorkerHost(deps);
}
